import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import { GuidelinesGate } from "@/components/GuidelinesGate";
import { FeedSection, BoardSection, PollsSection, EventsSection, VolunteersSection, DirectorySection, MapSection } from "@/pages/community/sections";
import { MessagesSquare, Bell, BarChart2, Calendar, HandHeart, BookUser, Map, Sparkles, ChevronRight } from "lucide-react";

const TABS = [
  { id: "feed", label: "Feed", icon: MessagesSquare },
  { id: "map", label: "Map", icon: Map },
  { id: "board", label: "Board", icon: Bell },
  { id: "polls", label: "Polls", icon: BarChart2 },
  { id: "events", label: "Events", icon: Calendar },
  { id: "volunteers", label: "Volunteer", icon: HandHeart },
  { id: "directory", label: "Directory", icon: BookUser },
];

export default function CommunityHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("feed");
  const [accepted, setAccepted] = useState(null);
  const isAdmin = ["community_admin", "platform_admin"].includes(user?.role);

  useEffect(() => {
    api.get("/community/guidelines/status").then((r) => setAccepted(r.data.accepted)).catch(() => setAccepted(false));
  }, []);

  if (accepted === null) return <Loader />;
  if (!user?.home_community_id)
    return (
      <div className="ll-fade-up px-5 pt-10 text-center" data-testid="community-no-home">
        <h1 className="text-2xl font-bold font-head">Join a community first</h1>
        <p className="text-[#111827]/60 mt-2">Set your home community to access the community module.</p>
      </div>
    );
  if (!accepted && !isAdmin) return <div className="ll-fade-up"><GuidelinesGate onAccepted={() => setAccepted(true)} /></div>;

  return (
    <div className="ll-fade-up" data-testid="community-hub">
      <div className="px-5 pt-8 pb-2">
        <h1 className="text-3xl font-bold font-head tracking-tight">Community</h1>
        <p className="text-[#111827]/60">Your neighbourhood, together.</p>
      </div>
      <div className="px-5 pb-1">
        <button
          data-testid="community-ask-guardian"
          onClick={() => navigate("/assistant")}
          className="w-full bg-[#0E7C66] text-white rounded-2xl px-4 py-3 flex items-center gap-3 active:scale-[0.99] transition-transform"
        >
          <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5" /></div>
          <div className="flex-1 text-left"><p className="font-bold text-sm">Ask Guardian AI</p><p className="text-xs text-white/80">Safety help, first aid & instant emergency actions</p></div>
          <ChevronRight className="w-5 h-5 shrink-0" />
        </button>
      </div>
      <div className="sticky top-0 z-30 bg-[#FFF7E6]/95 backdrop-blur-md py-2">
        <div className="flex gap-2 overflow-x-auto px-5 no-scrollbar">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`community-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-colors ${tab === t.id ? "bg-[#F97316] text-white" : "bg-white text-[#111827]/55 border border-[#111827]/10"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-5 mt-2">
        {tab === "feed" && <FeedSection />}
        {tab === "map" && <MapSection />}
        {tab === "board" && <BoardSection isAdmin={isAdmin} />}
        {tab === "polls" && <PollsSection isAdmin={isAdmin} />}
        {tab === "events" && <EventsSection isAdmin={isAdmin} />}
        {tab === "volunteers" && <VolunteersSection />}
        {tab === "directory" && <DirectorySection />}
      </div>
    </div>
  );
}
